import React, { useState, useEffect } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Layers, Loader2, Search, X, Percent } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

export const PortfolioOverlapPage = () => {
  const [fund1Query, setFund1Query] = useState("");
  const [fund2Query, setFund2Query] = useState("");
  const [fund1, setFund1] = useState(null);
  const [fund2, setFund2] = useState(null);
  const [fund1Results, setFund1Results] = useState([]);
  const [fund2Results, setFund2Results] = useState([]);
  const [overlap, setOverlap] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchFunds = async (query, setResults) => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/funds/search?q=${query}&limit=8`);
      setResults(res.data.funds || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchFunds(fund1Query, setFund1Results), 300);
    return () => clearTimeout(timer);
  }, [fund1Query]);

  useEffect(() => {
    const timer = setTimeout(() => searchFunds(fund2Query, setFund2Results), 300);
    return () => clearTimeout(timer);
  }, [fund2Query]);

  const selectFund = (fund, setFund, setQuery, setResults) => {
    setFund(fund);
    setQuery(fund.schemeName);
    setResults([]);
  };

  const calculateOverlap = () => {
    if (!fund1 || !fund2) return;
    
    setLoading(true);
    // Mock overlap calculation - in real app this would compare actual holdings
    setTimeout(() => {
      const mockOverlap = (Math.random() * 60 + 10).toFixed(1);
      const commonStocks = Math.floor(Math.random() * 30 + 5);
      setOverlap({
        percentage: mockOverlap,
        commonHoldings: commonStocks,
        uniqueInFund1: Math.floor(Math.random() * 20 + 10),
        uniqueInFund2: Math.floor(Math.random() * 20 + 10),
        topCommon: [
          "HDFC Bank Ltd", "ICICI Bank Ltd", "Reliance Industries", 
          "Infosys Ltd", "TCS Ltd"
        ].slice(0, Math.min(5, commonStocks))
      });
      setLoading(false);
    }, 1000);
  };

  const clearFund = (setFund, setQuery) => {
    setFund(null);
    setQuery("");
    setOverlap(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Portfolio <span className="gold-text">Overlap</span> Analysis
            </h1>
            <p className="text-muted-foreground">
              Check how much your funds overlap in holdings to avoid over-diversification
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Fund 1 Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Fund 1</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search fund..."
                        value={fund1Query}
                        onChange={(e) => setFund1Query(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {fund1 && (
                        <button
                          onClick={() => clearFund(setFund1, setFund1Query)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                    {fund1Results.length > 0 && !fund1 && (
                      <div className="absolute z-10 w-full bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {fund1Results.map((f) => (
                          <button
                            key={f.schemeCode}
                            onClick={() => selectFund(f, setFund1, setFund1Query, setFund1Results)}
                            className="w-full text-left px-3 py-2 hover:bg-secondary text-sm"
                          >
                            {f.schemeName}
                          </button>
                        ))}
                      </div>
                    )}
                    {fund1 && (
                      <p className="text-xs text-green-500">✓ Selected</p>
                    )}
                  </div>

                  {/* Fund 2 Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Fund 2</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search fund..."
                        value={fund2Query}
                        onChange={(e) => setFund2Query(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {fund2 && (
                        <button
                          onClick={() => clearFund(setFund2, setFund2Query)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                    {fund2Results.length > 0 && !fund2 && (
                      <div className="absolute z-10 w-full bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {fund2Results.map((f) => (
                          <button
                            key={f.schemeCode}
                            onClick={() => selectFund(f, setFund2, setFund2Query, setFund2Results)}
                            className="w-full text-left px-3 py-2 hover:bg-secondary text-sm"
                          >
                            {f.schemeName}
                          </button>
                        ))}
                      </div>
                    )}
                    {fund2 && (
                      <p className="text-xs text-green-500">✓ Selected</p>
                    )}
                  </div>
                </div>

                <Button 
                  onClick={calculateOverlap} 
                  disabled={!fund1 || !fund2 || loading}
                  className="w-full mt-6"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Layers className="w-4 h-4 mr-2" />
                  )}
                  Analyze Overlap
                </Button>
              </CardContent>
            </Card>

            {overlap && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Percent className="w-5 h-5 text-primary" />
                    Overlap Analysis Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Main Overlap Display */}
                  <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Portfolio Overlap</p>
                    <p className={`text-5xl font-bold ${
                      overlap.percentage > 50 ? 'text-red-500' : 
                      overlap.percentage > 30 ? 'text-yellow-500' : 'text-green-500'
                    }`}>
                      {overlap.percentage}%
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {overlap.percentage > 50 ? 'High overlap - Consider diversifying' : 
                       overlap.percentage > 30 ? 'Moderate overlap' : 'Low overlap - Well diversified'}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 rounded-lg bg-secondary">
                      <p className="text-2xl font-bold">{overlap.commonHoldings}</p>
                      <p className="text-xs text-muted-foreground">Common Stocks</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary">
                      <p className="text-2xl font-bold">{overlap.uniqueInFund1}</p>
                      <p className="text-xs text-muted-foreground">Unique in Fund 1</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary">
                      <p className="text-2xl font-bold">{overlap.uniqueInFund2}</p>
                      <p className="text-xs text-muted-foreground">Unique in Fund 2</p>
                    </div>
                  </div>

                  {/* Top Common Holdings */}
                  <div>
                    <p className="text-sm font-medium mb-3">Top Common Holdings</p>
                    <div className="flex flex-wrap gap-2">
                      {overlap.topCommon.map((stock, idx) => (
                        <span key={idx} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                          {stock}
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
